import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { usePullToRefresh, RefreshIndicator } from '../../hooks/usePullToRefresh';
import api from '../../services/api';
import { toast } from 'sonner';
import PaymentMethodChooser from '../../components/checkout/PaymentMethodChooser';
import { useBusinessTimezone, getTodayInTz } from '../../utils/businessTime';
import { 
  CreditCard, 
  Building2, 
  FileText, 
  Upload, 
  CheckCircle, 
  AlertCircle,
  AlertTriangle,
  Save,
  DollarSign,
  Loader2,
  ExternalLink,
  Banknote,
  Wallet,
  ArrowRight,
  Download,
  Calendar,
  Filter,
  X,
  History,
  ArrowUp,
  ArrowDown,
  Clock,
  Eye,
  Receipt
} from 'lucide-react';
import PayWithCreditButton from '../../components/parent/PayWithCreditButton';
import { ParentInvoiceList } from '../../components/parent/ParentInvoiceList';
import PaymentMethodOnFileForm from '../../components/PaymentMethodOnFileForm';
import { useFeatures } from '../../context/FeatureContext';

// Map the parent /invoices aggregator's invoice.type → the entity_type
// keys the self-service Pay-With-Credit endpoint expects. Returns null
// for invoice types we don't currently support draining credit against
// (the button is hidden in that case).
const invoiceTypeToEntityType = (t) => ({
  enrollment: 'enrollment',
  tournament: 'tournament_registration',
  lane_booking: 'lane_booking',
  small_group: 'small_group_invoice',
  camp_registration: 'camp_registration',
}[String(t || '').toLowerCase()] || null);

const ParentBilling = () => {
  const businessTz = useBusinessTimezone();
  const { isFeatureEnabled } = useFeatures();
  const achEnabled = isFeatureEnabled('payments.ach');
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  // iter306q: in-flight payments map so the FE can hard-block the Pay
  // Now button per invoice and show "Payment in progress · clears ~X"
  // banner instead. Key = matching invoice id (entity_id) → row.
  const [inFlightByInvoice, setInFlightByInvoice] = useState({});

  // iter266 — derive the saved card/ACH details so the Confirm Payment
  // dialogs can show "Pays from VISA ••1234" inline. Prefer the user's
  // default_payment_method when it matches the type; else first found.
  const savedCard = (
    paymentMethods.find((pm) => pm.type === 'card' && pm.is_default) ||
    paymentMethods.find((pm) => pm.type === 'card')
  );
  const savedAch = (
    paymentMethods.find((pm) => pm.type === 'bank_account' && pm.is_default) ||
    paymentMethods.find((pm) => pm.type === 'bank_account')
  );
  const [openingPaymentMethod, setOpeningPaymentMethod] = useState(false);
  const [setupIntent, setSetupIntent] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Credit history state
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditSummary, setCreditSummary] = useState({ current_balance: 0, total_issued: 0, total_used: 0 });
  const [loadingCredits, setLoadingCredits] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
    isFiltered: false
  });
  // iter307bb — Invoice status filter (defaults to "overdue" so parents
  // see what needs attention first). "all" shows the complete history.

  const [formData, setFormData] = useState({
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: ''
  });

  // Canonical status helpers — defensive against the data variations we've
  // seen in the wild (legacy "completed", Stripe "succeeded", payment_transactions
  // "complete", explicit invoices "paid", small-group invoices "void", lane
  // bookings "refunded", etc.)
  // A partially refunded item was paid — part of the money came back, so it
  // must not look unpaid (and must not sit in "void" like a full refund).
  const PAID_SET = ['paid', 'completed', 'complete', 'succeeded', 'partially_refunded'];
  const VOID_SET = ['void', 'refunded', 'cancelled', 'canceled'];
  const isPaid = (s) => PAID_SET.includes(String(s || '').toLowerCase());
  const isVoid = (s) => VOID_SET.includes(String(s || '').toLowerCase());
  const statusBadgeClass = (s) => {
    if (isPaid(s)) return 'bg-green-100 text-green-700';
    const v = String(s || '').toLowerCase();
    if (v === 'pending') return 'bg-amber-100 text-amber-700';
    if (v === 'unpaid') return 'bg-orange-100 text-orange-700';
    if (v === 'overdue') return 'bg-red-100 text-red-700';
    if (isVoid(s)) return 'bg-slate-100 text-slate-500 line-through';
    return 'bg-slate-100 text-slate-600';
  };
  const statusLabel = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'complete' || v === 'completed' || v === 'succeeded') return 'Paid';
    if (v === 'unpaid') return 'Unpaid';
    return s;
  };

  const { refreshing, onTouchStart, onTouchEnd } = usePullToRefresh(async () => {
    await fetchData();
  });

  useEffect(() => {
    fetchData();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get('session_id');
    const paymentType = searchParams.get('type');
    const savedPm = searchParams.get('saved_pm');
    const status = searchParams.get('status');
    if (sessionId) {
      verifyStripePayment(sessionId, paymentType);
    } else if (savedPm === '1') {
      // iter263: paid via saved card/ACH (no Stripe redirect). The
      // PaymentIntent webhook will flip the invoice to paid. Show a
      // confirmation, clean the URL, and refresh after a short delay
      // so the new "paid" state appears.
      if (status === 'processing') {
        toast.info('Bank payment submitted — it may take 3–5 business days to settle.', { duration: 6000 });
      } else {
        toast.success('Payment successful — paid with your saved method.');
      }
      window.history.replaceState({}, '', '/parent/billing?tab=invoices');
      // Webhook arrival is fast in practice but can lag a beat.
      setTimeout(() => fetchData(), 1500);
    }
  }, [searchParams]);
  
  const verifyStripePayment = async (sessionId, paymentType) => {
    setVerifyingPayment(true);
    try {
      const response = await api.get(`/billing/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        window.history.replaceState({}, '', '/parent/billing?tab=invoices');
        fetchData();
      } else if (response.data.payment_status === 'unpaid' || response.data.payment_status === 'pending') {
        // ACH/bank payments may take time — retry after a short delay
        toast.info('Payment is being processed. Checking again...');
        await new Promise(r => setTimeout(r, 3000));
        const retry = await api.get(`/billing/payment/status/${sessionId}`);
        if (retry.data.payment_status === 'paid') {
          toast.success('Payment confirmed!');
          window.history.replaceState({}, '', '/parent/billing?tab=invoices');
          fetchData();
        } else {
          toast.info('Payment is still processing. It may take a few minutes for bank transfers. Please refresh the page shortly.');
          window.history.replaceState({}, '', '/parent/billing?tab=invoices');
          fetchData();
        }
      } else {
        toast.error('Payment was not completed. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    } finally {
      setVerifyingPayment(false);
    }
  };

  const fetchData = async () => {
    try {
      // Build invoice URL with date filters
      let invoiceUrl = '/invoices';
      const params = new URLSearchParams();
      if (dateFilter.startDate) params.append('start_date', dateFilter.startDate);
      if (dateFilter.endDate) params.append('end_date', dateFilter.endDate);
      if (params.toString()) invoiceUrl += `?${params.toString()}`;
      
      const [billingRes, docsRes, invoicesRes, paymentMethodsRes, inFlightRes] = await Promise.all([
        api.get('/parent/billing-info'),
        api.get('/parent/documents/status'),
        api.get(invoiceUrl),
        api.get('/stripe/payment-methods').catch(() => ({ data: { payment_methods: [] } })),
        // iter306q: surface in-flight payments so we can disable Pay Now
        // before the parent clicks (avoids the 409 surprise).
        api.get('/parent/payments/in-flight').catch(() => ({ data: { in_flight_payments: [] } })),
      ]);
      setBillingInfo(billingRes.data);
      setDocumentStatus(docsRes.data);
      setInvoices(invoicesRes.data);
      const _pms = paymentMethodsRes.data.payment_methods || [];
      setPaymentMethods(_pms);
      // iter375 — Enforce "one payment method required". The Stripe hosted
      // portal can leave a parent with zero methods; when that happens we
      // flag it (banner) and trigger a throttled "required" email.
      if (_pms.length === 0 && paymentMethodsRes.data.has_stripe_customer !== false) {
        api.post('/stripe/payment-methods/required-check').catch(() => {});
      }
      const _byInv = {};
      (inFlightRes.data?.in_flight_payments || []).forEach((p) => {
        const key = p.entity_id || p.invoice_id;
        if (key) _byInv[String(key)] = p;
      });
      setInFlightByInvoice(_byInv);
      
      if (billingRes.data) {
        const addr = billingRes.data.billing_address;
        let streetAddr = '';
        let city = '';
        let state = '';
        let zip = '';
        
        if (typeof addr === 'object' && addr !== null) {
          streetAddr = addr.street || '';
          city = addr.city || '';
          state = addr.state || '';
          zip = addr.zip || '';
        } else if (typeof addr === 'string' && addr) {
          const lines = addr.split('\n');
          if (lines.length >= 1) streetAddr = lines[0];
          if (lines.length >= 2) {
            const lastLine = lines[lines.length - 1];
            const match = lastLine.match(/^([^,]+),?\s*([A-Z]{2})?\s*(\d{5})?$/i);
            if (match) {
              city = match[1]?.trim() || '';
              state = match[2]?.trim() || '';
              zip = match[3]?.trim() || '';
            } else {
              city = lastLine;
            }
          }
        }
        
        setFormData(prev => ({
          ...prev,
          billing_address: streetAddr,
          billing_city: city,
          billing_state: state,
          billing_zip: zip
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const beginPaymentMethodSetup = async (paymentMethodType = 'card') => {
    setOpeningPaymentMethod(true);
    try {
      const response = await api.post('/stripe/payment-methods/setup-intent', {
        payment_method_type: paymentMethodType,
      });
      setSetupIntent(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start the secure payment form');
    } finally {
      setOpeningPaymentMethod(false);
    }
  };

  const confirmPaymentMethodSetup = async (setupIntentId, paymentMethodId) => {
    const response = await api.post('/stripe/payment-methods/confirm-setup-intent', {
      setup_intent_id: setupIntentId,
      payment_method_id: paymentMethodId,
    });
    setPaymentMethods(response.data.payment_methods || []);
    setSetupIntent(null);
    toast.success('Payment method saved securely');
  };

  const removePaymentMethod = async (paymentMethodId) => {
    try {
      const response = await api.delete(`/stripe/payment-methods/${paymentMethodId}`);
      setPaymentMethods(response.data.payment_methods || []);
      toast.success('Payment method removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'This payment method could not be removed');
    }
  };

  const handlePayInvoice = async (invoiceId, amount) => {
    // Open a small confirm dialog so the parent picks card-or-ACH (upfront
    // choice replaces the old post-payment ACH surcharge refund).
    const inv = invoices.find((i) => i.id === invoiceId) || {
      id: invoiceId,
      total_amount: amount,
      description: 'Invoice',
    };
    setSinglePayPmChoice(null);
    setSinglePayInvoice(inv);
  };

  const handleConfirmSinglePay = async () => {
    if (!singlePayInvoice || !singlePayPmChoice) return;
    setSinglePayLoading(true);
    setPayingInvoice(singlePayInvoice.id);
    try {
      const response = await api.post('/billing/pay-invoice', {
        invoice_id: singlePayInvoice.id,
        return_url: window.location.origin + '/parent/billing?tab=invoices',
        payment_method_choice: singlePayPmChoice,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      // iter303: server returns 409 with structured detail when a
      // payment for this invoice is already in flight (esp. ACH which
      // takes 4-5 business days to clear). Surface that to the parent
      // clearly so they don't double-pay.
      const detail = error.response?.data?.detail;
      if (
        error.response?.status === 409 &&
        detail &&
        typeof detail === 'object' &&
        detail.code === 'duplicate_payment_in_flight'
      ) {
        setSinglePayInvoice(null);
        setDuplicatePaymentInfo({
          invoice: singlePayInvoice,
          status: detail.existing_payment_status,
          amount: detail.existing_payment_amount,
          method: detail.existing_payment_method,
          initiated_at: detail.initiated_at,
          expected_clearing_date: detail.expected_clearing_date,
          message: detail.message,
        });
      } else {
        toast.error(
          (typeof detail === 'string' ? detail : detail?.message) ||
          'Failed to create payment session',
        );
      }
      setPayingInvoice(null);
      setSinglePayLoading(false);
    }
  };

  // ---- Pay All Outstanding ----
  // Bundles every CURRENTLY-DUE-OR-OVERDUE invoice into a single Stripe
  // Checkout. The /api/billing/pay-all-outstanding endpoint stores the
  // bundle in `payment_bundles`; the webhook walks it and marks each row
  // paid in one go.
  const [showPayAllDialog, setShowPayAllDialog] = useState(false);
  const [payAllLoading, setPayAllLoading] = useState(false);
  // Card-vs-ACH choice for the bundle. `null` until the parent picks one
  // — Continue button stays disabled in the meantime.
  const [payAllPmChoice, setPayAllPmChoice] = useState(null);
  // iter287: The dialog content is now driven by the BACKEND
  // /billing/outstanding-summary response — what the backend says it
  // will bundle. Previously the dialog rendered the local invoices list
  // (which could disagree with the backend's filter), leading to the
  // production "charged $1,260 but email said $365" incident where the
  // bundle silently picked up future-dated invoices the parent never
  // saw before redirect. Now the parent confirms the EXACT items + total
  // the backend will charge.
  const [bundlePreview, setBundlePreview] = useState(null); // {items, outstanding_total, outstanding_count}
  const [bundlePreviewLoading, setBundlePreviewLoading] = useState(false);
  // Per-invoice "Pay" button needs the same upfront choice. We open a
  // small confirm dialog before redirecting so the parent always sees the
  // total they'll be charged.
  const [singlePayInvoice, setSinglePayInvoice] = useState(null);
  const [singlePayPmChoice, setSinglePayPmChoice] = useState(null);
  const [singlePayLoading, setSinglePayLoading] = useState(false);
  // iter303: surfaces an in-flight ACH/processing payment so the
  // parent doesn't initiate a duplicate. Server returns this in the
  // 409 detail body when a prior PaymentIntent is still settling.
  const [duplicatePaymentInfo, setDuplicatePaymentInfo] = useState(null);
  // Banner + "Pay All Outstanding ($X)" use the same backend set the bundle charges.
  const [payAllSummary, setPayAllSummary] = useState({ outstanding_count: 0, outstanding_total: 0 });
  useEffect(() => {
    api.get('/billing/outstanding-summary').then((r) => setPayAllSummary(r.data || {})).catch(() => {});
  }, [invoices]);
  const outstandingCount = Number(payAllSummary.outstanding_count || 0);
  const outstandingTotal = Number(payAllSummary.outstanding_total || 0);

  // iter287: open the Pay-All dialog AND fetch the authoritative bundle
  // preview from the backend at the same time. Continue button stays
  // disabled until the preview returns.
  const openPayAllDialog = async () => {
    setPayAllPmChoice(null);
    setBundlePreview(null);
    setShowPayAllDialog(true);
    setBundlePreviewLoading(true);
    try {
      const { data } = await api.get('/billing/outstanding-summary');
      setBundlePreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load outstanding invoices');
      setShowPayAllDialog(false);
    } finally {
      setBundlePreviewLoading(false);
    }
  };

  const handlePayAllOutstanding = async () => {
    if (!payAllPmChoice) return;
    // iter287 belt-and-braces: never POST if the preview is empty or
    // still loading. Prevents a race where a stale dialog click submits
    // before the preview returned.
    if (!bundlePreview || bundlePreviewLoading
        || !bundlePreview.items || bundlePreview.items.length === 0) {
      toast.error('No invoices are currently due to bundle.');
      return;
    }
    setPayAllLoading(true);
    try {
      const res = await api.post('/billing/pay-all-outstanding', {
        return_url: window.location.origin + '/parent/billing?tab=invoices',
        payment_method_choice: payAllPmChoice,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create bundled checkout');
      setPayAllLoading(false);
      setShowPayAllDialog(false);
    }
  };

  const handleSaveBilling = async (e) => {
    e.preventDefault();
    if (!formData.billing_address || !formData.billing_city || !formData.billing_state || !formData.billing_zip) {
      toast.error('Please fill in all billing address fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        payment_method: 'stripe', // Default to Stripe
        billing_address: {
          street: formData.billing_address,
          city: formData.billing_city,
          state: formData.billing_state,
          zip: formData.billing_zip
        }
      };
      await api.put('/parent/billing-info', payload);
      toast.success('Billing address saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save billing info');
    } finally {
      setSaving(false);
    }
  };
  
  const handleApplyDateFilter = () => {
    setDateFilter(prev => ({ ...prev, isFiltered: true }));
    fetchData();
  };
  
  const handleClearDateFilter = () => {
    setDateFilter({ startDate: '', endDate: '', isFiltered: false });
    // Refetch without filters
    setTimeout(fetchData, 0);
  };
  
  const fetchCreditHistory = async () => {
    setLoadingCredits(true);
    try {
      const response = await api.get('/parent/credit-history');
      setCreditHistory(response.data.credit_history || []);
      setCreditSummary({
        current_balance: response.data.current_balance || 0,
        total_issued: response.data.total_issued || 0,
        total_used: response.data.total_used || 0
      });
    } catch (error) {
      console.error('Error fetching credit history:', error);
      setCreditHistory([]);
    } finally {
      setLoadingCredits(false);
    }
  };
  
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (dateFilter.startDate) params.append('start_date', dateFilter.startDate);
      if (dateFilter.endDate) params.append('end_date', dateFilter.endDate);
      
      const response = await api.get(`/invoices/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `invoice_history_${getTodayInTz(businessTz)}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Invoice history exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export: ${error.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    setUploadingDoc(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      await api.post(`/parent/documents/upload?document_type=${docType}`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${docType === 'address_proof' ? 'Address proof' : 'School ID'} uploaded successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Billing & Payments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Billing & Payments">
      <div className="space-y-6" data-testid="parent-billing-page" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <RefreshIndicator refreshing={refreshing} />
        {/* Payment Verification Banner */}
        {verifyingPayment && (
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-blue-700 font-medium">Verifying your payment...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter262 — Pay All nudge. When the parent/customer has 2+ open
            invoices we surface a prominent banner at the very top so it
            beats the per-row Pay buttons. One click → bundled Stripe
            checkout for everything outstanding. */}
        {outstandingCount >= 2 && (
          <Card
            className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/60 shadow-sm"
            data-testid="pay-all-banner"
          >
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-emerald-600/10 p-2 shrink-0">
                    <DollarSign className="h-6 w-6 text-emerald-700" />
                  </div>
                  <div>
                    <p
                      className="font-heading text-base sm:text-lg font-semibold text-emerald-900"
                      data-testid="pay-all-banner-headline"
                    >
                      You have {outstandingCount} open invoice{outstandingCount === 1 ? '' : 's'} totaling{' '}
                      <span className="tabular-nums">{cs()}{outstandingTotal.toFixed(2)}</span>
                    </p>
                    <p className="text-sm text-emerald-800/80 mt-0.5">
                      Settle everything in one checkout — saves time, fewer card-fee charges, and avoids late notices.
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={openPayAllDialog}
                  disabled={payAllLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm w-full sm:w-auto shrink-0"
                  data-testid="pay-all-banner-cta"
                >
                  <DollarSign className="h-4 w-4 mr-1.5" />
                  Pay All Now
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        <Tabs defaultValue="payment" onValueChange={(v) => { if (v === 'credits') fetchCreditHistory(); }}>
          <TabsList className="flex w-full max-w-2xl overflow-x-auto">
            <TabsTrigger value="payment" data-testid="tab-payment" className="text-xs lg:text-sm whitespace-nowrap">
              <CreditCard className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Payment Method</span>
              <span className="lg:hidden">Payment</span>
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="text-xs lg:text-sm whitespace-nowrap">
              <FileText className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Documents</span>
              <span className="lg:hidden">Docs</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices" className="text-xs lg:text-sm whitespace-nowrap">
              <DollarSign className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Invoices</span>
              <span className="lg:hidden">Bills</span>
            </TabsTrigger>
            <TabsTrigger value="credits" data-testid="tab-credits" className="text-xs lg:text-sm whitespace-nowrap">
              <History className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Credit History</span>
              <span className="lg:hidden">Credits</span>
            </TabsTrigger>
          </TabsList>

          {/* Payment Method Tab */}
          <TabsContent value="payment" className="mt-6">
            <div className="space-y-6">
              <Card className="border border-slate-200">
                <CardHeader className="border-b border-slate-100 p-3 lg:p-6">
                  <div className="space-y-3">
                    <div className="min-w-0">
                      <CardTitle className="font-heading text-base lg:text-lg">Payment Methods</CardTitle>
                      <p className="text-xs lg:text-sm text-slate-500 mt-0.5">
                        Manage cards and bank accounts here. Stripe securely stores your payment details.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => beginPaymentMethodSetup('card')}
                        disabled={openingPaymentMethod}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 shrink-0 h-8 text-xs lg:h-10 lg:text-sm"
                        data-testid="add-card-btn"
                      >
                        {openingPaymentMethod ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening...</> : <><CreditCard className="h-4 w-4 mr-2" />Add card</>}
                      </Button>
                      {achEnabled && (
                        <Button
                          onClick={() => beginPaymentMethodSetup('us_bank_account')}
                          disabled={openingPaymentMethod}
                          size="sm"
                          variant="outline"
                          className="shrink-0 h-8 text-xs lg:h-10 lg:text-sm"
                          data-testid="add-ach-btn"
                        >
                          <Building2 className="h-4 w-4 mr-2" />Add bank
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 lg:p-6">
                  {paymentMethods.length === 0 && (
                    <div
                      className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
                      data-testid="payment-method-required-banner"
                    >
                      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-red-800">A payment method is required</p>
                        <p className="text-red-700 mt-0.5">
                          There is no card or bank account on file. Please add one using
                          “Add card” to keep your enrollments, bookings and
                          auto-payments active.
                        </p>
                      </div>
                    </div>
                  )}
                  {paymentMethods.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-700 mb-3">Saved Payment Methods:</p>
                      {paymentMethods.map((pm) => (
                        <div 
                          key={pm.id} 
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            pm.is_default ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'
                          }`}
                          data-testid={`payment-method-${pm.id}`}
                        >
                          <div className="flex items-center gap-3">
                            {pm.type === 'card' ? (
                              <CreditCard className={`h-6 w-6 ${pm.is_default ? 'text-blue-600' : 'text-slate-500'}`} />
                            ) : (
                              <Building2 className={`h-6 w-6 ${pm.is_default ? 'text-blue-600' : 'text-slate-500'}`} />
                            )}
                            <div>
                              <p className="font-medium text-slate-900">
                                {pm.type === 'card' 
                                  ? `${pm.brand?.charAt(0).toUpperCase()}${pm.brand?.slice(1)} •••• ${pm.last4}`
                                  : `${pm.bank_name} •••• ${pm.last4}`
                                }
                              </p>
                              {pm.type === 'card' && (
                                <p className="text-sm text-slate-500">Expires {pm.exp_month}/{pm.exp_year}</p>
                              )}
                              {pm.type === 'bank_account' && (
                                <p className="text-sm text-slate-500">{pm.account_type} account</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {pm.is_default ? (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full" data-testid={`pm-preferred-badge-${pm.id}`}>
                                Preferred
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`pm-make-preferred-${pm.id}`}
                                onClick={async () => {
                                  try {
                                    await api.post(`/stripe/set-default-payment-method/${pm.id}`);
                                    toast.success('Preferred payment method updated');
                                    const res = await api.get('/stripe/payment-methods');
                                    setPaymentMethods(res.data.payment_methods || []);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Failed to update preferred method');
                                  }
                                }}
                              >
                                Make Preferred
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-700 hover:text-red-800"
                              data-testid={`pm-remove-${pm.id}`}
                              onClick={() => {
                                const warning = 'Academy policy requires an active enrollment to keep a payment method on file. Remove this method only after adding a replacement, or after enrollment ends.';
                                if (window.confirm(warning)) removePaymentMethod(pm.id);
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 font-medium">No payment methods saved</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Add a card here. Bank-account setup is available when included in your plan.
                      </p>
                      {/* Auto-pay safety net: when the admin has flagged
                          this parent as enrolled in auto-pay (typical
                          after a new program enrollment), surface a
                          stronger warning so the parent knows monthly
                          billing will fail until a method is added. */}
                      {billingInfo?.auto_pay_acknowledged && (
                        <div
                          className="mt-4 border border-amber-300 bg-amber-50 rounded-md p-3 text-left text-sm text-amber-900 flex items-start gap-2"
                          data-testid="autopay-add-method-warning"
                        >
                          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" />
                          <div>
                            <p className="font-medium">Auto-pay enabled — payment method required</p>
                            <p className="text-amber-800 text-xs mt-1">
                              Your account is set up for monthly auto-payment of enrollment invoices.
                              Please add a card or bank account so next month's invoice can be billed
                              automatically. Otherwise your invoice will be marked unpaid.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-2">Accepted Payment Methods:</p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <CreditCard className="h-4 w-4" />
                        <span>Credit/Debit Cards</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Building2 className="h-4 w-4" />
                        <span>Bank Account (ACH)</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Manual Payment Methods Info */}
              <Card className="border border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg">Alternative Payment Options</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    These payment methods can be used for in-person payments. Contact the academy for details.
                  </p>
                </CardHeader>
                <CardContent className="p-4 lg:p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
                    <div className="p-3 lg:p-4 bg-slate-50 rounded-lg text-center">
                      <Banknote className="h-6 w-6 lg:h-8 lg:w-8 text-green-600 mx-auto mb-1.5" />
                      <p className="font-medium text-sm lg:text-base text-slate-700">Cash</p>
                    </div>
                    <div className="p-3 lg:p-4 bg-slate-50 rounded-lg text-center">
                      <FileText className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600 mx-auto mb-1.5" />
                      <p className="font-medium text-sm lg:text-base text-slate-700">Check</p>
                    </div>
                    <div className="p-3 lg:p-4 bg-slate-50 rounded-lg text-center">
                      <DollarSign className="h-6 w-6 lg:h-8 lg:w-8 text-purple-600 mx-auto mb-1.5" />
                      <p className="font-medium text-sm lg:text-base text-slate-700">Zelle</p>
                    </div>
                    <div className="p-3 lg:p-4 bg-slate-50 rounded-lg text-center">
                      <Wallet className="h-6 w-6 lg:h-8 lg:w-8 text-cyan-600 mx-auto mb-1.5" />
                      <p className="font-medium text-sm lg:text-base text-slate-700">Venmo</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Billing Address Form */}
              <form onSubmit={handleSaveBilling}>
                <Card className="border border-slate-200">
                  <CardHeader className="border-b border-slate-100">
                    <CardTitle className="font-heading text-lg">Billing Address</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 lg:p-6 space-y-4">
                    <div className="space-y-2">
                      <Label>Street Address</Label>
                      <Input
                        value={formData.billing_address}
                        onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                        placeholder="123 Main Street"
                        required
                        data-testid="billing-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={formData.billing_city}
                          onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })}
                          placeholder="Edison"
                          required
                          data-testid="billing-city-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input
                          value={formData.billing_state}
                          onChange={(e) => setFormData({ ...formData, billing_state: e.target.value })}
                          placeholder="NJ"
                          required
                          data-testid="billing-state-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP Code</Label>
                        <Input
                          value={formData.billing_zip}
                          onChange={(e) => setFormData({ ...formData, billing_zip: e.target.value })}
                          placeholder="08844"
                          required
                          data-testid="billing-zip-input"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end pt-4">
                      <Button
                        type="submit"
                        disabled={saving}
                        className="bg-blue-500 hover:bg-blue-600"
                        data-testid="save-billing-btn"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Address
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </form>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-6">
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg">Edison, NJ Residency Documents</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Edison residents are required to upload proof of address and student school ID.
                </p>
              </CardHeader>
              <CardContent className="p-4 lg:p-6">
                {!documentStatus?.is_edison_resident ? (
                  <div className="p-6 text-center bg-slate-50 rounded-lg">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Document upload is only required for Edison, NJ residents.</p>
                    <p className="text-sm text-slate-500 mt-2">
                      Based on your billing address, document upload is not required.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {documentStatus?.documents_required && !documentStatus?.all_documents_uploaded && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700">
                          <AlertCircle className="h-5 w-5" />
                          <span className="font-medium">Documents Required</span>
                        </div>
                        <p className="text-sm text-amber-600 mt-1">
                          Please upload both address proof and school ID to complete your Edison residency verification.
                        </p>
                      </div>
                    )}

                    {documentStatus?.all_documents_uploaded && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">All Documents Uploaded</span>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Address Proof */}
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <h4 className="font-medium text-slate-900 mb-2">Address Proof</h4>
                        <p className="text-sm text-slate-500 mb-4">
                          Utility bill, bank statement, or government document showing Edison, NJ address.
                        </p>
                        {documentStatus?.address_proof_uploaded ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span>Uploaded</span>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                              <Upload className="h-5 w-5 text-slate-400" />
                              <span className="text-slate-600">Click to upload</span>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleDocumentUpload('address_proof', e.target.files[0])}
                              disabled={uploadingDoc}
                              data-testid="address-proof-input"
                            />
                          </label>
                        )}
                      </div>

                      {/* School ID */}
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <h4 className="font-medium text-slate-900 mb-2">Student School ID</h4>
                        <p className="text-sm text-slate-500 mb-4">
                          Current school ID card or enrollment letter for the student.
                        </p>
                        {documentStatus?.school_id_uploaded ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            <span>Uploaded</span>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                              <Upload className="h-5 w-5 text-slate-400" />
                              <span className="text-slate-600">Click to upload</span>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleDocumentUpload('school_id', e.target.files[0])}
                              disabled={uploadingDoc}
                              data-testid="school-id-input"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-heading text-lg">Invoices & Payment History</CardTitle>
                    <CardDescription>View and export your billing history</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {outstandingCount > 0 && (
                      <Button
                        size="sm"
                        onClick={openPayAllDialog}
                        disabled={payAllLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                        data-testid="pay-all-outstanding-btn"
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Pay All Outstanding ({cs()}{outstandingTotal.toFixed(2)})
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('excel')}
                      disabled={exporting || invoices.length === 0}
                      data-testid="export-excel-btn"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport('pdf')}
                      disabled={exporting || invoices.length === 0}
                      data-testid="export-pdf-btn"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </div>
                
                {/* Date Filter */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Filter className="h-4 w-4" />
                      <span>Filter by date:</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Label className="text-xs text-slate-500">From:</Label>
                        <Input
                          type="date"
                          value={dateFilter.startDate}
                          onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                          className="h-8 w-36 text-sm"
                          data-testid="filter-start-date"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <Label className="text-xs text-slate-500">To:</Label>
                        <Input
                          type="date"
                          value={dateFilter.endDate}
                          onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                          className="h-8 w-36 text-sm"
                          data-testid="filter-end-date"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleApplyDateFilter}
                        disabled={!dateFilter.startDate && !dateFilter.endDate}
                        className="h-8"
                      >
                        <Calendar className="h-3 w-3 mr-1" />
                        Apply
                      </Button>
                      {dateFilter.isFiltered && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleClearDateFilter}
                          className="h-8 text-slate-500"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ParentInvoiceList
                  refreshKey={invoices}
                  startDate={dateFilter.startDate}
                  endDate={dateFilter.endDate}
                  onPay={(inv) => {
                    setSinglePayPmChoice(null);
                    setSinglePayInvoice({
                      id: inv.pay_id,
                      total_amount: inv.accounting?.balance_remaining,
                      amount: inv.accounting?.balance_remaining,
                      description: inv.tournament_name || inv.description || 'Invoice',
                      type: inv.type,
                    });
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credit History Tab */}
          <TabsContent value="credits" className="mt-6">
            <div className="space-y-6">
              {/* Credit Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">
                <Card className="border border-green-200 bg-green-50">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 lg:p-3 bg-green-100 rounded-full">
                        <Wallet className="h-5 w-5 lg:h-6 lg:w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs lg:text-sm text-green-600 font-medium">Current Balance</p>
                        <p className="text-xl lg:text-2xl font-bold text-green-700" data-testid="credit-balance">
                          {cs()}{creditSummary.current_balance.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-blue-200 bg-blue-50">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 lg:p-3 bg-blue-100 rounded-full">
                        <ArrowUp className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs lg:text-sm text-blue-600 font-medium">Total Issued</p>
                        <p className="text-xl lg:text-2xl font-bold text-blue-700">
                          {cs()}{creditSummary.total_issued.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-slate-200 bg-slate-50">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 lg:p-3 bg-slate-100 rounded-full">
                        <ArrowDown className="h-5 w-5 lg:h-6 lg:w-6 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-xs lg:text-sm text-slate-600 font-medium">Total Used</p>
                        <p className="text-xl lg:text-2xl font-bold text-slate-700">
                          {cs()}{creditSummary.total_used.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Credit History List */}
              <Card className="border border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Credit History
                  </CardTitle>
                  <CardDescription>
                    Track all credits issued and applied to your account
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {loadingCredits ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    </div>
                  ) : creditHistory.length === 0 ? (
                    <div className="p-12 text-center">
                      <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 font-medium">No credit history yet</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Credits will appear here when issued for enrollment changes
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {creditHistory.map((credit) => (
                        <div 
                          key={credit.id} 
                          className="p-4 hover:bg-slate-50 transition-colors"
                          data-testid={`credit-entry-${credit.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-full ${
                                credit.type === 'credit' 
                                  ? 'bg-green-100' 
                                  : 'bg-orange-100'
                              }`}>
                                {credit.type === 'credit' ? (
                                  <ArrowUp className="h-4 w-4 text-green-600" />
                                ) : (
                                  <ArrowDown className="h-4 w-4 text-orange-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">
                                  {credit.description}
                                </p>
                                <p className="text-sm text-slate-500">
                                  {credit.player_name}
                                  {credit.program_name && ` • ${credit.program_name}`}
                                </p>
                                {credit.reason && (
                                  <p className="text-sm text-slate-400 mt-1">
                                    Reason: {credit.reason}
                                  </p>
                                )}
                                {credit.issued_by && (
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    Issued by {credit.issued_by}
                                  </p>
                                )}
                                {credit.billing_details?.classes_remaining !== undefined && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    {credit.billing_details.classes_remaining} of {credit.billing_details.total_classes} classes remaining
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-semibold ${
                                credit.type === 'credit' 
                                  ? 'text-green-600' 
                                  : 'text-orange-600'
                              }`}>
                                {credit.type === 'credit' ? '+' : '-'}{cs()}{credit.amount.toFixed(2)}
                              </p>
                              <p className="text-xs text-slate-400 flex items-center justify-end gap-1 mt-1">
                                <Clock className="h-3 w-3" />
                                {credit.timestamp 
                                  ? new Date(credit.timestamp).toLocaleDateString('en-US', {
                                      timeZone: businessTz,
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'N/A'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* View Invoice Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-blue-600" />
                Invoice Details
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-slate-500">Type</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedInvoice.revenue_type === 'Subscription' ? 'bg-blue-100 text-blue-700' :
                        selectedInvoice.revenue_type === 'Lane Booking' ? 'bg-purple-100 text-purple-700' :
                        selectedInvoice.revenue_type === 'Tournament' ? 'bg-orange-100 text-orange-700' :
                        selectedInvoice.revenue_type === 'Small Group' ? 'bg-teal-100 text-teal-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {selectedInvoice.revenue_type || selectedInvoice.type}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Status</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusBadgeClass(selectedInvoice.status)}`}>
                        {statusLabel(selectedInvoice.status)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-3">
                    <p className="text-sm text-slate-500">Description</p>
                    <p className="font-medium">{selectedInvoice.description || selectedInvoice.type}</p>
                    {selectedInvoice.player_name && (
                      <p className="text-sm text-slate-500 mt-1">Player: {selectedInvoice.player_name}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 border-t pt-3">
                    <div>
                      <p className="text-sm text-slate-500">Amount</p>
                      <p className="text-xl font-bold text-green-600">{cs()}{(selectedInvoice.total_paid || selectedInvoice.total_amount || 0).toFixed(2)}</p>
                      {selectedInvoice.frequency_multiplier > 1 && (
                        <p className="text-xs text-slate-500 mt-1" data-testid="invoice-detail-freq">
                          {cs()}{(selectedInvoice.monthly_fee || 0).toFixed(2)}/month × {selectedInvoice.frequency_multiplier} months
                          {selectedInvoice.is_prorated && <> (first month prorated)</>}
                        </p>
                      )}
                      {selectedInvoice.surcharge_amount > 0 && (
                        <p className="text-xs text-slate-400">Base: {cs()}{(selectedInvoice.base_amount || 0).toFixed(2)} + {cs()}{(selectedInvoice.surcharge_amount || 0).toFixed(2)} CC fee</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Due Date</p>
                      <p className="font-medium">
                        {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString('en-US', { timeZone: businessTz }) : '-'}
                      </p>
                      {selectedInvoice.next_billing_date && selectedInvoice.frequency_multiplier > 1 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Next bill: {new Date(selectedInvoice.next_billing_date).toLocaleDateString('en-US', { timeZone: businessTz })}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isPaid(selectedInvoice.status) && selectedInvoice.payment_method_type && (
                    <div className="border-t pt-3 flex items-center gap-3">
                      <div>
                        <p className="text-sm text-slate-500">Payment Method</p>
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          selectedInvoice.payment_method_type === 'us_bank_account' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                        }`}>
                          {selectedInvoice.payment_method_type === 'us_bank_account' ? 'ACH / Bank Transfer' : 'Credit Card'}
                        </span>
                      </div>
                      {selectedInvoice.payment_date && (
                        <div>
                          <p className="text-sm text-slate-500">Paid On</p>
                          <p className="font-medium">{new Date(selectedInvoice.payment_date).toLocaleDateString('en-US', { timeZone: businessTz })}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {isPaid(selectedInvoice.status) && (
                    <div className="border-t pt-3 space-y-2">
                      <p className="text-sm font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        Payment Complete
                      </p>
                      {selectedInvoice.paid_at && (
                        <p className="text-sm text-slate-600">
                          Paid on {new Date(selectedInvoice.paid_at).toLocaleDateString('en-US', { timeZone: businessTz })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
                  {(['pending','overdue','unpaid'].includes(String(selectedInvoice.status || '').toLowerCase())) && selectedInvoice.total_amount > 0 && (
                    <Button
                      onClick={() => {
                        setViewDialog(false);
                        handlePayInvoice(selectedInvoice.id, selectedInvoice.total_amount);
                      }}
                      className={selectedInvoice.status === 'overdue' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
                    >
                      Pay Now
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pay All Outstanding — confirm dialog */}
        <Dialog open={showPayAllDialog} onOpenChange={setShowPayAllDialog}>
          <DialogContent className="max-w-lg" data-testid="pay-all-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Pay All Outstanding Invoices
              </DialogTitle>
              <DialogDescription>
                {bundlePreviewLoading ? (
                  <>Calculating what's currently due…</>
                ) : (
                  <>
                    {bundlePreview?.outstanding_count || 0}{' '}
                    invoice{(bundlePreview?.outstanding_count || 0) === 1 ? '' : 's'} currently due ·
                    one Stripe checkout. Future-dated invoices (e.g. next month's enrollment,
                    upcoming camps) are NOT included.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {bundlePreviewLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading…
              </div>
            ) : (bundlePreview?.items || []).length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm" data-testid="pay-all-empty">
                Nothing currently due to bundle. Future invoices will appear here on their due date.
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-80 overflow-auto pr-1">
                  {(bundlePreview?.items || []).map((it) => (
                    <div
                      key={`pay-all-${it.type}-${it.source_id}`}
                      className="flex items-center justify-between gap-3 p-2 rounded border border-slate-100 bg-slate-50"
                      data-testid={`pay-all-line-${it.source_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {it.label || it.type}
                        </p>
                        {it.due_date && (
                          <p className="text-xs text-slate-500 truncate">
                            Due: {String(it.due_date).slice(0, 10)}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-slate-900">
                          {cs()}{(Number(it.amount) || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-slate-200 mt-3">
                  <span className="text-sm text-slate-600">Total to charge now</span>
                  <span className="text-2xl font-bold text-slate-900" data-testid="pay-all-total">
                    {cs()}{(Number(bundlePreview?.outstanding_total) || 0).toFixed(2)}
                  </span>
                </div>
              </>
            )}

            {(bundlePreview?.items || []).length > 0 && (
              <div className="pt-3 border-t border-slate-200 mt-3">
                <PaymentMethodChooser
                  baseAmount={Number(bundlePreview?.outstanding_total) || 0}
                  value={payAllPmChoice}
                  onChange={setPayAllPmChoice}
                  disabled={payAllLoading}
                  savedCardLast4={savedCard?.last4 || null}
                  savedCardBrand={savedCard?.brand || null}
                  savedAchLast4={savedAch?.last4 || null}
                  savedAchBank={savedAch?.bank_name || null}
                />
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPayAllDialog(false)} disabled={payAllLoading}>
                Cancel
              </Button>
              <Button
                onClick={handlePayAllOutstanding}
                disabled={
                  payAllLoading
                  || bundlePreviewLoading
                  || !bundlePreview
                  || (bundlePreview?.items || []).length === 0
                  || !payAllPmChoice
                }
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-pay-all-btn"
              >
                {payAllLoading ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Redirecting…</>
                ) : (
                  <>
                    Confirm & Pay {cs()}{(Number(bundlePreview?.outstanding_total) || 0).toFixed(2)}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Per-invoice "Pay" — Card/ACH chooser before redirect */}
        <Dialog
          open={!!singlePayInvoice}
          onOpenChange={(o) => { if (!o) { setSinglePayInvoice(null); setSinglePayLoading(false); setPayingInvoice(null); } }}
        >
          <DialogContent className="max-w-md" data-testid="single-pay-dialog">
            <DialogHeader>
              <DialogTitle>Confirm Payment</DialogTitle>
              <DialogDescription>
                {singlePayInvoice?.description || 'Invoice payment'}
              </DialogDescription>
            </DialogHeader>
            <PaymentMethodChooser
              baseAmount={Number(singlePayInvoice?.total_amount) || 0}
              value={singlePayPmChoice}
              onChange={setSinglePayPmChoice}
              disabled={singlePayLoading}
              savedCardLast4={savedCard?.last4 || null}
              savedCardBrand={savedCard?.brand || null}
              savedAchLast4={savedAch?.last4 || null}
              savedAchBank={savedAch?.bank_name || null}
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setSinglePayInvoice(null); setPayingInvoice(null); }}
                disabled={singlePayLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSinglePay}
                disabled={!singlePayPmChoice || singlePayLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="confirm-single-pay-btn"
              >
                {singlePayLoading
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Redirecting…</>
                  : <>Continue to Checkout<ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter303: duplicate-payment-in-flight modal. Surfaces when a
            prior payment for this invoice is still ACH-clearing
            (4-5 business days) or otherwise processing. Blocks the
            parent from initiating a second charge — they MUST wait or
            contact admin. */}
        <Dialog
          open={!!duplicatePaymentInfo}
          onOpenChange={(o) => { if (!o) setDuplicatePaymentInfo(null); }}
        >
          <DialogContent className="max-w-md" data-testid="duplicate-payment-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertCircle className="h-5 w-5" />
                Payment Already In Progress
              </DialogTitle>
            </DialogHeader>
            {duplicatePaymentInfo && (
              <div className="space-y-4 py-2">
                <p className="text-sm text-slate-700">
                  {duplicatePaymentInfo.message}
                </p>
                <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  {duplicatePaymentInfo.invoice?.description && (
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Invoice:</span>
                      <span className="font-medium text-right">{duplicatePaymentInfo.invoice.description}</span>
                    </div>
                  )}
                  {duplicatePaymentInfo.amount != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Amount in flight:</span>
                      <span className="font-medium">{cs()}{Number(duplicatePaymentInfo.amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">Method:</span>
                    <span className="font-medium capitalize">
                      {duplicatePaymentInfo.method === 'us_bank_account' || duplicatePaymentInfo.method === 'ach'
                        ? 'ACH bank debit'
                        : duplicatePaymentInfo.method || 'Card'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status:</span>
                    <span className="font-medium capitalize">{duplicatePaymentInfo.status}</span>
                  </div>
                  {duplicatePaymentInfo.initiated_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Initiated:</span>
                      <span className="font-medium">
                        {new Date(duplicatePaymentInfo.initiated_at).toLocaleDateString('en-US', { timeZone: businessTz, month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  {duplicatePaymentInfo.expected_clearing_date && (
                    <div className="flex justify-between border-t border-amber-200 pt-2 mt-2">
                      <span className="text-amber-700 font-medium">Expected to clear by:</span>
                      <span className="font-bold text-amber-700">
                        {new Date(duplicatePaymentInfo.expected_clearing_date + 'T12:00:00').toLocaleDateString('en-US', { timeZone: businessTz, weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Initiating a second payment would charge you twice. If you believe the
                  existing payment failed, please contact your academy admin to cancel it
                  before re-paying.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDuplicatePaymentInfo(null)}
                data-testid="duplicate-payment-dismiss"
              >
                Got it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!setupIntent} onOpenChange={(open) => { if (!open) setSetupIntent(null); }}>
          <DialogContent className="max-w-lg" data-testid="payment-method-setup-dialog">
            <DialogHeader>
              <DialogTitle>Save payment method</DialogTitle>
              <DialogDescription>
                Your details are entered directly into Stripe's secure fields. VyomOS does not store card or bank-account numbers.
              </DialogDescription>
            </DialogHeader>
            {setupIntent && (
              <PaymentMethodOnFileForm
                clientSecret={setupIntent.client_secret}
                publishableKey={setupIntent.publishable_key}
                onConfirmed={confirmPaymentMethodSetup}
                onError={(message) => toast.error(message)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ParentBilling;
